import { inject } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { QuestionnaireData } from "src/app/models/QuestionnaireData.model";
import { combineLatest, filter, map, pipe, switchMap, tap, withLatestFrom } from "rxjs";
import { DataSummaryApiService } from "../services/data-summary-api-service";
import { AnsweredQuestionnaireData, QuestionnaireDataByQuestion } from "src/app/models/QuestionnaireDataByQuestions.model";
import { EQuestionnaireStatus, QuestionnaireRow } from "src/app/models/questionnaireGrid.model";
import { EReplyStatus, Recipient, Recipients } from "src/app/models/recipients.model";
import { toSignal } from "@angular/core/rxjs-interop";
import { TostesStore } from "src/app/store/tostes-store";
import { TostPositionEnum, TostTypeEnum } from "src/app/shared/models/tostes.model";
import { QuestionnairesGridStore } from "./questionnairesGrid-store";
import { ApiExposureData } from "src/app/models/apiExposureData.model";

type FilterSubmissionOption = {
    recipientId: number;
    label: string;
    filterAnswerValue: any;
    dateAnswered?: string | null;
};
export interface DataSummaryState {
    questionnaireDataSummary?: QuestionnaireDataByQuestion,
    apiExposureData?: ApiExposureData,
    answeredTheQuestionnaire?: Recipient[],
    DisplayState: string,
    questionsDic?: any,
    currentId?: number,
    currentStep: number,
    currentFilter?: EReplyStatus,
    selectedQuestionId?: number | string,
    showComparison?: boolean,
    loadingStatusAnswered: boolean;
    loadingSummaryData: boolean;
    selectedFilterFieldValue?: string | number | null;
    recipientsToFilterId?: number;
    recipientIdToFilter?: number | null;
}
export class DataSummaryStore extends ComponentStore<DataSummaryState> {
    #dataSummaryApiService = inject(DataSummaryApiService);
    #questionnairesGridStore = inject(QuestionnairesGridStore);
    #tostesStore = inject(TostesStore);

    readonly id$ = this.select((x) => x.questionnaireDataSummary?.id);
    readonly apiExposureData$ = this.select((x) => x.apiExposureData);
    readonly filterFieldQuestionsData$ = this.select((x) => x.questionnaireDataSummary?.stepsData.filter(s => s.questionsData.some(q => q.question.isFilterField))
        .flatMap(s => s.questionsData.filter(q => q.question.isFilterField).map(q => q.question))[0]);
    readonly filterFieldAnswerOptions$ = this.select((x) => {
        const selectedRecipientId = x.recipientsToFilterId; // אם יש, מצמצם למשתמש אחד
        const steps = x.questionnaireDataSummary?.stepsData ?? [];

        const filterQuestionData = steps
            .flatMap(s => s.questionsData ?? [])
            .find(qd => !!qd?.question?.isFilterField);

        const answersByRecipients = filterQuestionData?.recipients ?? [];

        const relevant = selectedRecipientId == null
            ? answersByRecipients
            : answersByRecipients.filter(r => r?.recipient?.user.id === selectedRecipientId);

        // חילוץ ערך התשובה (תתאימי לפי סוג שאלת סינון – כאן דוגמה למולטי)
        const values = relevant
            .map(r => r?.answer?.multiOptionsAnswer ?? r?.answer?.textAnswer ?? r?.answer?.intAnswer)
            .filter(v => v !== null && v !== undefined && v !== '');

        // ייחודיות + מיון
        return Array.from(new Set(values)).sort((a: any, b: any) => `${a}`.localeCompare(`${b}`));
    });
    readonly filterFieldSubmissionOptions$ = this.select((x): FilterSubmissionOption[] => {
        const userId = x.recipientsToFilterId; // אצלך זה userId
        const summary = x.questionnaireDataSummary;
        if (!userId || !summary) return [];

        const steps = summary.stepsData ?? [];

        // כל ההגשות (recipient records) של המשתמש הזה
        const userRecipients = (summary.recipients ?? []).filter(r => r?.user?.id === userId);
        const recipientIdSet = new Set(userRecipients.map(r => r.id));

        // questionData של שאלת הפילטור
        const filterQuestionData = steps
            .flatMap(s => s.questionsData ?? [])
            .find(qd => !!qd?.question?.isFilterField);

        const filterAnswers = filterQuestionData?.recipients ?? [];

        // מפה: recipientId -> value
        const mapRecipientToValue = new Map<number, any>();
        for (const r of filterAnswers) {
            const rid = r?.recipient?.id;
            if (!rid || !recipientIdSet.has(rid)) continue;

            const val =
                r?.answer?.multiOptionsAnswer ??
                r?.answer?.textAnswer ??
                r?.answer?.intAnswer ??
                r?.answer?.dateAnswerNTZ ??
                null;

            mapRecipientToValue.set(rid, val);
        }

        return userRecipients
            .map(rec => {
                const val = mapRecipientToValue.get(rec.id);

                // תווית: ערך + תאריך כדי להבדיל בין הגשות זהות
                const parts = [
                    (val != null && `${val}`.trim() !== '') ? `${val}` : '—'
                ];
                if (rec.dateAnswered) {
                    parts.push(new Date(rec.dateAnswered).toLocaleDateString('he-IL'));
                }

                return {
                    recipientId: rec.id,
                    filterAnswerValue: val,
                    dateAnswered: rec.dateAnswered ? new Date(rec.dateAnswered).toISOString() : null,
                    label: parts.join(' · ')
                };
            })
            .sort((a, b) => `${b.dateAnswered ?? ''}`.localeCompare(`${a.dateAnswered ?? ''}`));
    });
    readonly selectedFilterSubmissionOption$ = this.select(
        this.filterFieldSubmissionOptions$,         // [{ recipientId, label, ... }]
        this.select(x => x.recipientIdToFilter),    // number | null
        (options, selectedRecipientId) => {
            if (!selectedRecipientId) return null;
            return options.find(o => o.recipientId === selectedRecipientId) ?? null;
        }
    );
    readonly filterFieldAnswersData$ = this.select((x) => {
        const userId = x.recipientsToFilterId; // userId
        const steps = x.questionnaireDataSummary?.stepsData ?? [];

        const filterQuestionData = steps
            .flatMap(s => s.questionsData ?? [])
            .find(qd => !!qd?.question?.isFilterField);

        const answersByRecipients = filterQuestionData?.recipients ?? [];

        if (userId == null) return answersByRecipients;

        return answersByRecipients.filter(r => r?.recipient?.user?.id === userId);
    });
    readonly questionsData$ = this.select((x) => {
        const steps = x.questionnaireDataSummary?.stepsData ?? [];
        const currentQuestions = steps[x.currentStep]?.questionsData ?? [];
        const rid = x.recipientIdToFilter;

        console.log('[Store] recipientIdToFilter=', rid);
        console.log('[Store] BEFORE recipients counts=', currentQuestions.map(q => (q.recipients ?? []).length));

        if (!rid) return currentQuestions;

        const filtered = currentQuestions.map(qd => ({
            ...qd,
            recipients: (qd.recipients ?? []).filter(rr => rr?.recipient?.id === rid),
        }));

        console.log('[Store] AFTER recipients counts=', filtered.map(q => (q.recipients ?? []).length));
        return filtered;
    });
    readonly recipientsAnsweredApiExposureQuestionsData$ = this.select((x) => x.apiExposureData?.recipients);
    readonly isLoadedCurretStep$ = this.select((x) => x.questionnaireDataSummary?.stepsData[x.currentStep]?.isLoaded);
    readonly questionnarieParentDetails$ = this.select((x) => { return { name: x.questionnaireDataSummary?.parentName, createdDate: x.questionnaireDataSummary?.parentCreateDate } });
    readonly isQuestionnarieParent$ = this.select((x) => { return x.questionnaireDataSummary?.parentName != null && x.questionnaireDataSummary?.parentCreateDate != null });
    readonly questionnarieDetails$ = this.select((x) => { return { name: x.questionnaireDataSummary?.name, createdDate: x.questionnaireDataSummary?.createDate } });
    readonly recipients$ = this.select(({ questionnaireDataSummary }) => questionnaireDataSummary?.recipients.filter(r => r.statusReply != EReplyStatus.NotStarted));
    readonly recipientsGroupbyUsers$ = this.select(({ questionnaireDataSummary }) => {
        const recipients = questionnaireDataSummary?.recipients ?? [];

        const filtered = recipients.filter(r => r.statusReply !== EReplyStatus.NotStarted);

        const uniqByUserId = new Map<string | number, typeof filtered[number]>();
        for (const r of filtered) {
            if (r.user.id == null) continue;          // אם אין userId – אפשר לדלג/לטפל אחרת
            if (!uniqByUserId.has(r.user.id)) {
                uniqByUserId.set(r.user.id, r);         // שומר את הראשון לכל userId
            }
        }

        return Array.from(uniqByUserId.values());
    });
    readonly selectedFilterFieldValue$ = this.select(x => x.selectedFilterFieldValue);
    readonly answeredTheQuestionnaire$ = this.select((x) => x.answeredTheQuestionnaire);
    readonly InProgressAnsweredTheQuestionnaire$ = this.select((x) => x.answeredTheQuestionnaire?.filter(x => x.statusReply == EReplyStatus.InProgress));
    readonly finishAnsweredTheQuestionnaire$ = this.select((x) => x.answeredTheQuestionnaire?.filter(x => x.statusReply == EReplyStatus.Finished));
    readonly notStartedAnsweredTheQuestionnaire$ = this.select((x) => x.answeredTheQuestionnaire?.filter(x => x.statusReply == EReplyStatus.NotStarted));
    readonly createDate$ = this.select((x) => x.questionnaireDataSummary?.createDate);
    readonly currentFilter$ = this.select((x) => x.currentFilter);
    readonly filterRecipientsByReply$ = combineLatest([this.recipients$, this.currentFilter$]).pipe(
        map(([contact, filter]) => {
            if (filter)
                return contact?.filter(x => (x.statusReply == EReplyStatus.Finished && filter == EReplyStatus.Finished) || (x.statusReply == EReplyStatus.InProgress && filter == EReplyStatus.InProgress))
            //need check if this logic is ok
            return contact;
        }));

    readonly name$ = this.select((x) => x.questionnaireDataSummary?.name);
    readonly stepsNames$ = this.select((x) => x.questionnaireDataSummary?.stepsData.map(s => s.name));
    readonly recipientsToFilterId$ = this.select((x) => x.recipientsToFilterId);
    readonly selectedQuestionId$ = this.select((x) => x.selectedQuestionId);
    readonly currentStep$ = this.select((x) => x.currentStep);
    readonly filterRecipients$ = this.select((x) => x.questionnaireDataSummary?.recipients.filter(r => {
        return x.recipientsToFilterId == undefined || r.user.id == x.recipientsToFilterId
    }));
    readonly displayState$ = this.select((x) => x.DisplayState);
    readonly showComparison$ = this.select((x) => x.showComparison);
    readonly isDataForAllSig$ = toSignal(this.recipients$.pipe(map(r => r?.length != 0 && r?.length != undefined)));
    readonly loadingStatusAnswered$ = this.select((x) => x.loadingStatusAnswered);
    readonly loadingData$ = this.select((x) => x.loadingSummaryData);
    readonly currentId$ = this.select((x) => x.currentId);
    readonly recipientIdToFilter$ = this.select(x => x.recipientIdToFilter);


    constructor() {
        super({
            loadingStatusAnswered: false,
            loadingSummaryData: false,
            currentStep: 0,
            DisplayState: ""
        });
    }

    readonly getAnsweredQuestionnaireData = this.effect<number>(
        pipe(
            tap(() =>
                this.patchState({
                    loadingStatusAnswered: true
                })),
            switchMap((id) => this.#dataSummaryApiService.getAnsweredQuestionnaireData(id)),
            tapResponse((data: Recipient[]) =>
                this.patchState({
                    answeredTheQuestionnaire: data,
                    loadingStatusAnswered: false
                }),
                (error) => {
                    this.patchState({
                        loadingStatusAnswered: false
                    })
                }),
        )
    );

    readonly getQuestionDataSummaryFirst = this.effect<number>(
        pipe(
            tap(() =>
                this.patchState({
                    loadingSummaryData: true
                })),
            map(id => {
                var guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Processing data, please wait.", he: "מעבד נתונים , אנא המתן" }, position: TostPositionEnum.TopRight, type: TostTypeEnum.LOAD })
                return { id, guid }
            }),
            switchMap((props) => this.#dataSummaryApiService.getQuestionnaireDataSummary(props.id, 1).pipe(
                tapResponse((data: QuestionnaireDataByQuestion) => {

                    this.patchState({ questionnaireDataSummary: data, loadingSummaryData: false });
                    this.#tostesStore.removeTostByGuid(props.guid);
                    this.getQuestionDataSummaryAll(props.id);

                }, (error) => {
                    this.#tostesStore.addTost({
                        guid: this.#tostesStore.generateGuid(), message: { en: "Oops, something went wrong.Try again", he: "אופס, משהו השתבש.נסו שוב" }, position: TostPositionEnum.TopRight, type: TostTypeEnum.ERROR
                    });
                }
                ))
            )
        )
    );
    readonly getQuestionDataSummaryAll = this.effect<number>(
        pipe(
            switchMap((id) => this.#dataSummaryApiService.getQuestionnaireDataSummary(id, null).pipe(
                tapResponse((data: QuestionnaireDataByQuestion) => {
                    this.patchState({ questionnaireDataSummary: data });
                }, (error) => {
                    this.#tostesStore.addTost({
                        guid: this.#tostesStore.generateGuid(), message: { en: "Oops, something went wrong.Try again", he: "אופס, משהו השתבש.נסו שוב" }, position: TostPositionEnum.TopRight, type: TostTypeEnum.ERROR
                    });
                }
                ))
            )
        )
    );

    readonly getApiExposureData = this.effect<number>(
        pipe(
            switchMap((id) => this.#dataSummaryApiService.getApiExposureData(id).pipe(
                tapResponse((data: ApiExposureData) => {
                    this.patchState({ apiExposureData: data });
                }, (error) => {
                    this.#tostesStore.addTost({
                        guid: this.#tostesStore.generateGuid(), message: { en: "Oops, something went wrong.Try again", he: "אופס, משהו השתבש.נסו שוב" }, position: TostPositionEnum.TopRight, type: TostTypeEnum.ERROR
                    });
                }
                ))
            )
        )
    );

    readonly updateApiExposureSelected = this.effect<{ questionnaireId: number, recipients: any[] }>(
        pipe(
            switchMap(({ questionnaireId, recipients }) => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Updating API exposure data...", he: "מעדכן נתוני API..." }, position: TostPositionEnum.TopRight, type: TostTypeEnum.LOAD });
                return this.#dataSummaryApiService.updateApiExposureSelected(questionnaireId, recipients).pipe(
                    tapResponse((res) => {
                        this.#tostesStore.removeTostByGuid(guid);
                        this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: "Data updated successfully", he: "הנתונים עודכנו בהצלחה" }, position: TostPositionEnum.TopRight, type: TostTypeEnum.SUCCESS, seconds: 5 });
                        // Refresh data after successful update
                        this.getApiExposureData(questionnaireId);
                    }, (error) => {
                        this.#tostesStore.removeTostByGuid(guid);
                        this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: "Error updating data", he: "שגיאה בעדכון נתונים" }, position: TostPositionEnum.TopRight, type: TostTypeEnum.ERROR, seconds: 5 });
                    })
                );
            })
        )
    );

    readonly updateCurrentId = this.updater((state, id: number) => ({
        ...state,
        currentId: id
    }));

    readonly updateCurrentStep = this.updater((state, step: number) => ({
        ...state,
        currentStep: step
    }));
    readonly updateDisplayState = this.updater((state, currentNav: string) => ({
        ...state,
        DisplayState: currentNav
    }));
    readonly updateCurrentFilter = this.updater((state, status: EReplyStatus | undefined) => ({
        ...state,
        currentFilter: status
    }));
    // readonly updateCurrentRecipientIdToFilter = this.updater((state, recipientId: number | undefined) => ({
    //     ...state,
    //     recipientsToFilterId: recipientId
    // }));
    readonly updateSelectedQuestionId = this.updater((state, questionId: number | undefined) => ({
        ...state,
        selectedQuestionId: questionId?.toString()
    }));
    readonly updateshowComparison = this.updater((state, status: boolean | undefined) => ({
        ...state,
        showComparison: status
    }));
    updateSelectedFilterFieldValue(value: string | number | undefined) {
        this.patchState({ selectedFilterFieldValue: value ?? null });
    };
    readonly updateCurrentRecipientIdToFilter = this.updater((state, userId: number | undefined) => ({
        ...state,
        recipientsToFilterId: userId,     // userId
        recipientIdToFilter: null,        // reset submission
    }));
    readonly updateRecipientIdToFilter = this.updater((state, recipientId: number | undefined) => ({
        ...state,
        recipientIdToFilter: recipientId ?? null,
    }));

    readonly changeStatus = this.effect<{ recipId: number, recipName: string }>(
        pipe(
            withLatestFrom(this.id$, this.finishAnsweredTheQuestionnaire$),
            switchMap(([recipient, questionnaireId, finishAnsweredTheQuestionnaire]) => this.#dataSummaryApiService.changeRecipientsStatus(questionnaireId ?? 0, recipient.recipId)
                .pipe(
                    tapResponse((respons: { isDone: boolean, recipGuid: string }) => {
                        if (respons.isDone) {
                            this.patchState({
                                answeredTheQuestionnaire:
                                    [...this.state().answeredTheQuestionnaire?.map(x => { return x.id == recipient.recipId ? { ...x, statusReply: EReplyStatus.InProgress } as Recipient : x }) ?? []]
                            });
                            this.#questionnairesGridStore.updateRecipientStatus({ questionnaireId: questionnaireId ?? 0, recipId: recipient.recipId, recipGuid: respons.recipGuid, statusQuestionnaire: (finishAnsweredTheQuestionnaire?.length ?? 0) > 1 ? EQuestionnaireStatus.Respond : EQuestionnaireStatus.Distributed });
                            this.#tostesStore.addTost({
                                guid: this.#tostesStore.generateGuid(), message: {
                                    en: `Questionnaire status for ${recipient.recipName} has been successfully updated to 'In Progress'.`,
                                    he: `סטטוס השאלון עבור ${recipient.recipName} 'עודכן בהצלחה ל'בתהליך`
                                }, type: TostTypeEnum.SUCCESS, seconds: 5
                            });
                        }
                        else {
                            this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: `An error occurred while updating the questionnaire status for ${recipient.recipName}.`, he: `אירעה שגיאה בעת עדכון סטטוס השאלון עבור ${recipient.recipName}` }, type: TostTypeEnum.ERROR, seconds: 5 });
                        }
                    }, (error) => {
                        this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: `An error occurred while updating the questionnaire status for ${recipient.recipName}.`, he: `אירעה שגיאה בעת עדכון סטטוס השאלון עבור ${recipient.recipName}` }, type: TostTypeEnum.ERROR, seconds: 5 });
                    }))
            ))
    );
}
